import { Navigate, type RouteObject } from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import {
  CoInvestmentFullPaths,
  CoInvestmentPaths,
  CommonPaths,
  VegaModulesPaths,
} from "@vega-modules/paths";
import { ActivationNextStep } from "@vega-modules/scenes/ActivationNextStep/ActivationNextStep";
import { CoInvestmentNavigationWrapper } from "@vega-modules/scenes/CoInvestmentsGP/components/CoInvestmentNavigationWrapper/CoInvestmentNavigationWrapper";
import { AddNotesForCommitteePage } from "@vega-modules/scenes/CoInvestmentsGP/pages/AddNotesForCommitteePage";
import { AuditPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/AuditPage";
import { CoInvestConversationPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestConversationPage";
import { CoInvestmentLpNdaPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestmentLpNdaPage";
import { CoInvestmentsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestmentsPage";
import { CoInvestmentTrackingPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestmentTrackingPage";
import { CoInvestMessagesPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestMessagesPage";
import { CoInvestNotificationsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/CoInvestNotificationsPage";
import { ConfigurePage } from "@vega-modules/scenes/CoInvestmentsGP/pages/ConfigurePage";
import { DataRoomRoute } from "@vega-modules/scenes/CoInvestmentsGP/pages/dataRoomRoute";
import { DetailsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/DetailsPage";
import { EmailTemplatePage } from "@vega-modules/scenes/CoInvestmentsGP/pages/EmailTemplatePage";
import { FindProspectsFromSalesforcePage } from "@vega-modules/scenes/CoInvestmentsGP/pages/FindProspectsFromSalesforcePage";
import { InsightsLimitedPartnerPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/InsightsLimitedPartnerPage";
import { InsightsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/InsightsPage";
import { NdaManagementPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/NdaManagementPage";
import { OutreachPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/OutreachPage";
import { ProspectsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/ProspectsPage";
import { ReportPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/ReportPage";
import { SendDraftsPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/SendDraftsPage";
import { SharePage } from "@vega-modules/scenes/CoInvestmentsGP/pages/SharePage";
import { TrackPage } from "@vega-modules/scenes/CoInvestmentsGP/pages/TrackPage";
import { AuthenticatedConfigBanner } from "@vega-modules/scenes/ConfigBanner/AuthenticatedConfigBanner";
import { LogoutRoute } from "@vega-modules/scenes/Logout/LogoutRoute";
import { AuthCodeExchange } from "@vega-modules/scenes/OktaAuthorise/OktaAuthorise";
import { QueryRedirectHandler } from "@vega-modules/scenes/QueryRedirectHandler/QueryRedirectHandler";
import { GPPrivateRoute } from "@vega-modules/scenes/Router/GPPrivateRoute";
import { PageNotFound } from "@vega-modules/scenes/Router/PageNotFound";
import { PublicRoute } from "@vega-modules/scenes/Router/PublicRoute";
import { SettingsRoutes } from "@vega-modules/scenes/SettingsGP/Route/SettingsRoutes";
import { WelcomeTasksPage } from "@vega-modules/scenes/Welcome/WelcomeTasksPage";

import { Activate } from "../Login/Activate";
import { Login } from "../Login/Login";

const NdaManagementRoute = () => {
  const { featureFlag } = useFeatureFlag();
  const isNewDataRoomEnabled =
    featureFlag[FeatureFlag.COINVEST_DATA_ROOM_VD_22599];

  return isNewDataRoomEnabled ? <NdaManagementPage /> : <PageNotFound />;
};

const LpNdasRoute = () => {
  const { featureFlag } = useFeatureFlag();
  const isNewDataRoomEnabled =
    featureFlag[FeatureFlag.COINVEST_DATA_ROOM_VD_22599];

  return !isNewDataRoomEnabled ? <CoInvestmentLpNdaPage /> : <PageNotFound />;
};

const InsightsRoute = () => {
  const { featureFlag } = useFeatureFlag();
  const isInsightsEnabled = featureFlag[FeatureFlag.COINVEST_INSIGHTS_VD_26155];

  return isInsightsEnabled ? <InsightsPage /> : <PageNotFound />;
};

const InsightsLimitedPartnerRoute = () => {
  const { featureFlag } = useFeatureFlag();
  const isInsightsEnabled = featureFlag[FeatureFlag.COINVEST_INSIGHTS_VD_26155];

  return isInsightsEnabled ? <InsightsLimitedPartnerPage /> : <PageNotFound />;
};

const NewNavRoute = ({ element }: { element: RouteObject["element"] }) => {
  const { featureFlag } = useFeatureFlag();
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  return isNewNavEnabled ? element : <PageNotFound />;
};

const NotificationsRoute = () => {
  const { featureFlag } = useFeatureFlag();
  const isNotificationsEnabled =
    featureFlag[FeatureFlag.COINVEST_NOTIFICATIONS];

  return isNotificationsEnabled ? (
    <CoInvestNotificationsPage />
  ) : (
    <PageNotFound />
  );
};

const CoInvestmentLayout = () => (
  <GPPrivateRoute>
    <CoInvestmentNavigationWrapper />
  </GPPrivateRoute>
);

const FeatureFlaggedCoInvestmentLayout = ({
  feature,
  enabled = true,
}: {
  feature: FeatureFlag | FeatureFlag[];
  enabled?: boolean;
}) => {
  const { featureFlag, isLoading } = useFeatureFlag();

  if (isLoading) return null;

  const features = Array.isArray(feature) ? feature : [feature];
  const isMatch = features.every(
    (flag) => Boolean(featureFlag[flag]) === enabled,
  );

  return isMatch ? <CoInvestmentLayout /> : <PageNotFound />;
};

export const gpCoInvestRouteObjects: RouteObject[] = [
  {
    element: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
    path: CommonPaths.Login,
  },
  {
    element: (
      <PublicRoute>
        <Activate />
      </PublicRoute>
    ),
    path: CommonPaths.Activated,
  },
  {
    element: (
      <PublicRoute>
        <AuthCodeExchange
          portalType={FrontendPortal.COINVEST_WORKBENCH_PORTAL}
        />
      </PublicRoute>
    ),
    path: CommonPaths.OktaAuth,
  },
  {
    element: <Navigate replace to={CoInvestmentFullPaths.CoInvestments} />,
    path: CommonPaths.Home,
  },
  {
    element: (
      <GPPrivateRoute>
        <WelcomeTasksPage />
      </GPPrivateRoute>
    ),
    path: CommonPaths.Welcome,
  },
  {
    element: <CoInvestmentLayout />,
    path: CoInvestmentFullPaths.CoInvestments,
    children: [
      {
        element: (
          <>
            <AuthenticatedConfigBanner />
            <CoInvestmentsPage />
          </>
        ),
        index: true,
      },
      { element: <DetailsPage />, path: CoInvestmentPaths.Details },
      { element: <DataRoomRoute />, path: CoInvestmentPaths.DataRoom },
      { element: <ProspectsPage />, path: CoInvestmentPaths.Prospects },
      { element: <OutreachPage />, path: CoInvestmentPaths.Outreach },
      {
        element: <CoInvestmentTrackingPage />,
        path: CoInvestmentPaths.Tracking,
      },
      { element: <AuditPage />, path: CoInvestmentPaths.Audit },
      {
        element: <FindProspectsFromSalesforcePage />,
        path: CoInvestmentPaths.ProspectsFindFromSalesforce,
      },
      {
        element: <AddNotesForCommitteePage />,
        path: CoInvestmentPaths.ProspectsAddNotes,
      },
    ],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.COINVEST_DATA_ROOM_VD_22599}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentNdaManagement,
    children: [{ element: <NdaManagementRoute />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        enabled={false}
        feature={FeatureFlag.COINVEST_DATA_ROOM_VD_22599}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpNdas,
    children: [{ element: <LpNdasRoute />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.COINVEST_INSIGHTS_VD_26155}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentInsights,
    children: [{ element: <InsightsRoute />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.COINVEST_INSIGHTS_VD_26155}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentInsightsLimitedPartner,
    children: [{ element: <InsightsLimitedPartnerRoute />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentConfigure,
    children: [
      { element: <NewNavRoute element={<ConfigurePage />} />, index: true },
    ],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={[
          FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769,
          FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885,
        ]}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentEmailTemplates,
    children: [{ element: <EmailTemplatePage />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentShare,
    children: [
      { element: <NewNavRoute element={<SharePage />} />, index: true },
    ],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={[
          FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769,
          FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885,
        ]}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentShareSendDrafts,
    children: [{ element: <SendDraftsPage />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentTrack,
    children: [
      { element: <NewNavRoute element={<TrackPage />} />, index: true },
    ],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentReport,
    children: [
      { element: <NewNavRoute element={<ReportPage />} />, index: true },
    ],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.COINVEST_NOTIFICATIONS}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentNotifications,
    children: [{ element: <NotificationsRoute />, index: true }],
  },
  {
    element: (
      <FeatureFlaggedCoInvestmentLayout
        feature={FeatureFlag.COINVEST_MESSAGING_VD_28584}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentMessages,
    children: [
      { element: <CoInvestMessagesPage />, index: true },
      { element: <CoInvestConversationPage />, path: ":conversationId" },
    ],
  },
  {
    element: <SettingsRoutes />,
    path: `${VegaModulesPaths.Settings}/*`,
  },
  {
    element: <LogoutRoute source={FrontendPortal.COINVEST_WORKBENCH_PORTAL} />,
    path: CommonPaths.Logout,
  },
  { element: <QueryRedirectHandler />, path: CommonPaths.Redirect },
  {
    element: <ActivationNextStep portal={FrontendPortal.GP_PORTAL} />,
    path: CommonPaths.ActivationNextStep,
  },
  {
    element: <ActivationNextStep portal={FrontendPortal.GP_PORTAL} />,
    path: CommonPaths.RegisterOkta,
  },
  { element: <PageNotFound />, path: "*" },
];
